import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';

import { formatNumberThsndAndDec, formatNumberMagnitude } from '../helpers/stringFormat';
import { getMagnitude } from './valueFunctions';
import { addCurrencyFrequency } from '../helpers/currencyFormat';
import { Typography } from '@mui/material';
import { GreenRedCompactSwitch, TextFieldCustom } from '../helpers/formFields';
import firstCharToUpper, { makePlural } from '../helpers/stringFormat';
import { createSliderMarks, getAverageValue } from './formFunctions';
import { tooltip, generalTooltipText } from '../helpers/tooltip';

// const frequencyTransfer = {
//     daily: "day",
//     monthly: "month",
//     quarterly: "quarter",
//     yearly: "year",
// };

const RevenueValueDriverIRForm = forwardRef(({ currency, outcome, outcomeList, product, revenueType, editIRDriverId, editIRDriver, formActions, onSubmit }, ref) => {
    const [positiveDriver, setPositiveDriver] = useState(true);
    const [openOutcome, setOpenOutcome] = useState(false);

    const handleOutcomeChange = (event) => {
        formik.setFieldValue('outcome', event.target.value)
    };

    const handleOutcomeClose = () => {
        setOpenOutcome(false);
    };

    const handleOutcomeOpen = () => {
        setOpenOutcome(true);
    };

    const tooltipText = {
        opportunity: `The annual ${positiveDriver ? "created" : "lost"} opportunity when using your product instead of the competitive alternative. E.g. how large market is opened up for the customer when using your product.`,
        probability: "Probability of acheiving a sale or the expected market share in the market. E.g. the customer is expected to convert 10% of the opportunities or their market share is expected to be 10%.",
    }

    useEffect(() => {
        formik.setFieldValue("revenue_per_multiple", getAverageValue(revenueType.revenue_per_multiple))
        formik.setFieldValue("contribution_margin", getAverageValue(revenueType.contribution_margin))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revenueType]);

    useEffect(() => {
        formik.setFieldValue("outcome", outcomeList[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outcomeList]);

    useEffect(() => {
        if (editIRDriverId) {
            setPositiveDriver(editIRDriver["positive_driver"])

            formik.setFieldValue("multiple_value", editIRDriver["multiple_value"])
            formik.setFieldValue("revenue_per_multiple", editIRDriver["revenue_per_multiple"])
            formik.setFieldValue("contribution_margin", editIRDriver["contribution_margin"])
            formik.setFieldValue("probability", editIRDriver["probability"])
            formik.setFieldValue("units_needed", editIRDriver["units_needed"])
            formik.setFieldValue("time_needed", editIRDriver["time_needed"])
            formik.setFieldValue("positive_driver", editIRDriver["positive_driver"])
            formik.setFieldValue("outcome", editIRDriver["outcome"])
            formik.setFieldValue("driver_name", editIRDriver["driver_name"])
            formik.setFieldValue("comment", editIRDriver["comment"])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editIRDriverId])

    useEffect(() => {
        formik.setFieldValue("driver_name", `${positiveDriver ? 'Incremental' : 'Decremental'} revenue`)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positiveDriver])

    const initialValues = {
        id: null,
        revenue_type: 'incremental',
        outcome: '',
        driver_name: `${positiveDriver ? 'Incremental' : 'Decremental'} revenue`,
        comment: '',
        positive_driver: true,
        type: '',
        currency: currency,
        multiple: revenueType.multiple,
        multiple_value: 0,
        revenue_per_multiple: getAverageValue(revenueType.revenue_per_multiple),
        contribution_margin: getAverageValue(revenueType.contribution_margin),
        probability: 0,
        units_needed: 0,
        time_needed: 0,
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const validate = (values) => {
        const errors = {};

        if (!values.driver_name) {
            errors.driver_name = 'Driver must have a name';
        }

        if (!isNumeric(values.multiple_value)) {
            errors.multiple_value = 'Must be numeric value';
        } else if (!isPositive(values.multiple_value)) {
            errors.multiple_value = 'Must be positive or zero';
        }

        if (!isNumeric(values.contribution_margin)) {
            errors.contribution_margin = 'Must be numeric value';
        } else if (!isPositive(values.contribution_margin)) {
            errors.contribution_margin = 'Must be positive or zero';
        } else if (values.contribution_margin > 100) {
            errors.contribution_margin = 'Not more than 100';
        }

        if (!isNumeric(values.probability)) {
            errors.probability = 'Must be numeric value';
        } else if (!isPositive(values.probability)) {
            errors.probability = 'Must be positive or zero';
        }

        if (!isNumeric(values.units_needed)) {
            errors.units_needed = 'Must be numeric value';
        } else if (!isPositive(values.units_needed)) {
            errors.units_needed = 'Must be positive or zero';
        }

        if (!isNumeric(values.time_needed)) {
            errors.time_needed = 'Must be numeric value';
        } else if (!isPositive(values.time_needed)) {
            errors.time_needed = 'Must be positive or zero';
        }

        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
            setPositiveDriver(true)
            if (!outcome) {
                formik.setFieldValue("outcome", outcomeList[0] || '')
            }
            formik.setFieldValue("revenue_per_multiple", getAverageValue(revenueType.revenue_per_multiple))
            formik.setFieldValue("contribution_margin", getAverageValue(revenueType.contribution_margin))
        },
    }));

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ ml: 2, maxWidth: 1300 }}>
            <Grid container columnSpacing={2} alignItems="center" rowSpacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            2. Profit opportunity
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Grid container columnGap={2}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Revenue per {revenueType.multiple}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                {positiveDriver ? 'Annual opportunity' : 'Annual lost opportunity'}{tooltip(tooltipText.opportunity)}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Contribution margin{tooltip(generalTooltipText.contributionMargin)}
                            </Typography>
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={3}>
                            <Slider
                                name="revenue_per_multiple"
                                value={formik.values.revenue_per_multiple}
                                onChange={formik.handleChange}
                                valueLabelDisplay="auto"
                                marks={createSliderMarks(revenueType.revenue_per_multiple)}
                                min={revenueType.revenue_per_multiple.lower * 0}
                                max={revenueType.revenue_per_multiple.higher * 1.2}
                                step={getMagnitude(revenueType.revenue_per_multiple.lower) / 10}
                                valueLabelFormat={value => addCurrencyFrequency(formatNumberMagnitude(value), revenueType.revenue_per_multiple.currency, null)}
                                sx={{ maxWidth: 300 }}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextFieldCustom
                                name="multiple_value"
                                endAdornment={
                                    <Typography variant="caption">
                                        {makePlural(revenueType.multiple)}
                                    </Typography>
                                }
                                value={formik.values.multiple_value}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("multiple_value", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "multiple_value") && Boolean(getIn(formik.errors, "multiple_value"))}
                                helperText={getIn(formik.touched, "multiple_value") && getIn(formik.errors, "multiple_value")}
                                greenBackground={positiveDriver}
                                redBackground={!positiveDriver}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextFieldCustom
                                name="contribution_margin"
                                endAdornment="%"
                                value={formik.values.contribution_margin}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("contribution_margin", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "contribution_margin") && Boolean(getIn(formik.errors, "contribution_margin"))}
                                helperText={getIn(formik.touched, "contribution_margin") && getIn(formik.errors, "contribution_margin")}
                            />
                        </Grid>
                        <Grid item xs={7} />
                    </Grid>
                </Grid>
                {/* <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            {`4. ${positiveDriver ? 'Faster': 'Slower'} to market`}
                        </Typography>
                    </Box>
                </Grid> */}
                <Grid item xs={12}>
                    <Grid container columnGap={2}>
                        <Grid item xs={2}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Probability of acheiving a sale or Market share{tooltip(tooltipText.probability)}
                            </Typography>
                        </Grid>
                        {/* {product.revenue_model === 'transactional' ?
                            <Grid item xs={2}>
                                <Typography variant="caption">
                                    {firstCharToUpper(makePlural(product.unit))} of {product.name} needed for supplying opportunity
                                </Typography>
                            </Grid>
                            :
                            <Grid item xs={2}>
                                <Typography variant="caption">
                                    {firstCharToUpper(makePlural(frequencyTransfer[product.frequency]))} subscription of {product.name} needed for development
                                </Typography>
                            </Grid>
                        } */}
                        {product.unit.length > 0 ?
                            <Grid item xs={2}>
                                <Typography variant="caption">
                                    {firstCharToUpper(makePlural(product.unit))} of {product.name} needed for supplying opportunity
                                </Typography>
                            </Grid>
                            :
                            <Grid item xs={2} />
                        }
                        <Grid item xs={10} />
                        <Grid item xs={2}>
                            <TextFieldCustom
                                name="probability"
                                endAdornment="%"
                                value={formik.values.probability}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("probability", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "probability") && Boolean(getIn(formik.errors, "probability"))}
                                helperText={getIn(formik.touched, "probability") && getIn(formik.errors, "probability")}
                            />
                        </Grid>
                        {/* <Grid item xs={2}>
                            <TextFieldCustom
                                name="units_needed"
                                endAdornment={
                                    <Typography variant="caption">
                                        {product.revenue_model === 'transactional' ? makePlural(product.unit) : makePlural(frequencyTransfer[product.frequency])}
                                    </Typography>
                                }
                                value={formik.values.units_needed}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("units_needed", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "units_needed") && Boolean(getIn(formik.errors, "units_needed"))}
                                helperText={getIn(formik.touched, "units_needed") && getIn(formik.errors, "units_needed")}
                            />
                        </Grid> */}
                        {product.unit.length > 0 ?
                            <Grid item xs={2}>
                                <TextFieldCustom
                                    name="units_needed"
                                    endAdornment={
                                        <Typography variant="caption">
                                            {makePlural(product.unit)}
                                        </Typography>
                                    }
                                    value={formik.values.units_needed}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formik.setFieldValue("units_needed", formattedValue || '')
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={getIn(formik.errors, "units_needed") && Boolean(getIn(formik.errors, "units_needed"))}
                                    helperText={getIn(formik.touched, "units_needed") && getIn(formik.errors, "units_needed")}
                                />
                            </Grid>
                            :
                            <Grid item xs={2} />
                        }
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            3. Driver details
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={2}>
                    <Stack direction="column" gap={1}>
                        <Typography variant="caption" sx={{ mr: 1 }}>
                            Value driver{tooltip(generalTooltipText.differentiationValue)}
                        </Typography>
                        <GreenRedCompactSwitch
                            name="positive_driver"
                            leftLabel="Negative"
                            rightLabel="Positive"
                            value={formik.values.positive_driver}
                            onChange={(event) => {
                                formik.handleChange(event)
                                setPositiveDriver(!positiveDriver)
                            }}
                        />

                    </Stack>
                </Grid>
                <Grid item xs={10} />
                {!outcome &&
                    (<React.Fragment>
                        <Grid item xs={2}>
                            <Stack direction="column" gap={1}>
                                <Typography variant="caption">
                                    Outcome
                                </Typography>
                                <Box>
                                    <Select
                                        open={openOutcome}
                                        onClose={handleOutcomeClose}
                                        onOpen={handleOutcomeOpen}
                                        value={formik.values.outcome}
                                        onChange={handleOutcomeChange}
                                        size='small'
                                    >
                                        {outcomeList.map((outcome) => (
                                            <MenuItem key={outcome} value={outcome}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                    {outcome}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={10} />
                    </React.Fragment>)
                }
                <Grid item xs={12}>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Name of driver
                            </Typography>
                        </Grid>
                        <Grid item xs={5}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Comment
                            </Typography>
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="driver_name"
                                value={formik.values.driver_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "driver_name") && Boolean(getIn(formik.errors, "driver_name"))}
                                helperText={getIn(formik.touched, "driver_name") && getIn(formik.errors, "driver_name")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <TextFieldCustom
                                name="comment"
                                value={formik.values.comment}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "comment") && Boolean(getIn(formik.errors, "comment"))}
                                helperText={getIn(formik.touched, "comment") && getIn(formik.errors, "comment")}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Box sx={{ mt: 3, mb: 4 }}>
                {formActions()}
            </Box>
        </Box>
    )

})

export default RevenueValueDriverIRForm